import {
  SearchFilterCondition,
  SearchFilterDateRangePresetKey,
  searchFilterDateRangePresetKeys,
  SearchFilterField,
  SearchFilters,
  SearchFilterValue,
} from 'lib-shared-types-api';
import memoizeOne from 'memoize-one';
import moment from 'moment-timezone';

import {
  dateRangePresets,
  filterItems,
  filtersInitialState,
} from '../../components/filters/utils/definitions';
import { isDateFilterField } from '../date/isDateFilterField';
import { convertMsToDuration } from '../duration/convertDuration';

import { isCustomAttributeCondition } from './filterTypeGuards';

/** Returns the display `label` for a static filter `value`. */
const getStaticFilterLabel = memoizeOne(
  (field: SearchFilterField, value: string) => {
    switch (field) {
      case 'status':
      case 'initial_channel':
      case 'initial_direction':
      case 'user_type':
        return filterItems[field]?.find(({ id }) => id === value)?.name;

      case 'duration': {
        const duration = convertMsToDuration(parseInt(value, 10));
        const unit =
          duration.value === 1 ? duration.unit.slice(0, -1) : duration.unit;
        return `${duration.value} ${unit.toLowerCase()}`;
      }

      case 'numeric_id':
        return `#${value}`;

      default: {
        if (isDateFilterField(field)) {
          const isPreset = searchFilterDateRangePresetKeys.includes(
            value as SearchFilterDateRangePresetKey,
          );

          if (isPreset) {
            return dateRangePresets.find(({ key }) => key === value)?.label;
          }

          const isDateFormat = moment(value, 'YYYY-MM-DD', true).isValid();
          return moment(value).format(isDateFormat ? 'L' : 'L LT');
        }
        return '';
      }
    }
  },
);

/**
 * Adds missing labels to the list of search filter conditions. Only handles
 * static filters.
 */
export const enrichStaticFilters = memoizeOne(
  (filters?: SearchFilters): SearchFilters => {
    if (!filters || !filters.conditions.length) return filtersInitialState;

    const conditions: SearchFilterCondition[] = filters.conditions.map(
      (condition) => {
        if (isCustomAttributeCondition(condition)) {
          return condition;
        }

        const { field, operator, values, ...rest } = condition;

        const enrichedValues = values.map<SearchFilterValue>(
          ({ label: origLabel, value }) => {
            const label =
              origLabel || getStaticFilterLabel(field, value as string) || '';
            return { label, value };
          },
        );

        return {
          field,
          operator,
          values: enrichedValues,
          ...rest,
        } as SearchFilterCondition;
      },
    );

    return { strategy: filters.strategy, conditions };
  },
);
